import React, { useMemo, useState, useEffect, useContext } from 'react';
import '../../css/Dashboard.css';
import QueriesComponent from './QueriesComponent';
import FollowUpsComponent from './FollowUpsComponent';
import QueriesOverTimeComponent from './QueriesOverTimeComponent';
import StatsRow from '../dashboard/StatsRow';
import PaymentsRow from '../dashboard/PaymentsRow';
import DepartingPackages from '../dashboard/DepartingPackages';
import ByTypeChart from '../dashboard/ByTypeChart';
import ByAssigneeChart from '../dashboard/ByAssigneeChart';
import BookingStackedChart from '../dashboard/BookingStackedChart';
import AttendanceComponent from '../dashboard/AttendanceComponent';
import QuickCheckInModal from '../Attendance/QuickCheckInModal';
import QuickCheckOutModal from '../Attendance/QuickCheckOutModal';
import DateRangePicker from '../Attendance/DateRangePicker';
import CustomerInfoModal from '../Customer/CustomerInfoModal';
import { DashboardDataProvider } from './DashboardDataContext';
import { AuthContext } from '../../context/AuthContext';
import { fetchUsers } from '../../api/User';
import { User } from '../../types/User';
import { Customer } from '../../types/Customer';
import { fetchCustomerById } from '../../api/Customer';
import { usePermissions, useRole } from '../../hooks/usePermissions';
import { filterEnabledRegularUsers } from '../../utils/userFilters';
import { useTodayAttendance } from '../../hooks/useTodayAttendance';
import * as FiIcons from 'react-icons/fi';
import * as MdIcons from 'react-icons/md';

const FiCheckSquare = FiIcons.FiCheckSquare as unknown as React.FC;
const FiCalendar = FiIcons.FiCalendar as unknown as React.FC;
const MdCheckCircle = MdIcons.MdCheckCircle as unknown as React.FC;

const SalesDashboard = () => {
  const { user } = useContext(AuthContext);
  const permissions = usePermissions();
  const role = useRole();
  const getDefaultDateRange = () => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - 29);
    return {
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0],
    };
  };
  const defaultRange = getDefaultDateRange();
  const [startDate, setStartDate] = useState(defaultRange.start);
  const [endDate, setEndDate] = useState(defaultRange.end);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<number | undefined>();
  const [users, setUsers] = useState<User[]>([]);
  const [teamUsers, setTeamUsers] = useState<User[]>([]);
  const [showQuickCheckIn, setShowQuickCheckIn] = useState(false);
  const [showQuickCheckOut, setShowQuickCheckOut] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const {
    attendance: todayAttendance,
    loading: checkingAttendance,
    refresh: refreshTodayAttendance,
  } = useTodayAttendance(user?.id);

  const filters = useMemo(
    () => ({ from: startDate, to: endDate, assignee: selectedUserId ? String(selectedUserId) : undefined }),
    [endDate, selectedUserId, startDate]
  );
  const attendanceMarkedToday = Boolean(todayAttendance);
  const attendanceCheckedOutToday = Boolean(todayAttendance?.checkOutTime);

  useEffect(() => {
    const loadUsers = async () => {
      try {
        const userList = await fetchUsers();
        const allUsers = filterEnabledRegularUsers(Array.isArray(userList) ? userList : []);
        setUsers(allUsers);

        if (role === 'sales-manager' || role === 'ops') {
          const currentUserId = user?.id ? (typeof user.id === 'string' ? parseInt(user.id) : user.id) : 0;
          setTeamUsers(allUsers.filter((dashboardUser) => dashboardUser.managerId === currentUserId));
        }
      } catch (error) {
        console.error('Failed to load dashboard users:', error);
      }
    };

    loadUsers();
  }, [role, user?.id]);

  const openCustomerModal = async (customerId?: number) => {
    if (!customerId) return;

    try {
      const customer = await fetchCustomerById(customerId);
      setSelectedCustomer(customer);
    } catch (error) {
      console.error('Failed to load customer details:', error);
    }
  };

  return (
    <div className="db-container crm-page">
      <DashboardDataProvider filters={filters}>
        <div className="db-header">
          <div className="db-header-left">
            <h2>Dashboard</h2>
            <p className="db-header-subtitle">
              Today’s overview of customers, queries, follow-ups and bookings.
            </p>
          </div>

          <div className="db-header-right">
            <div className="filters-bar">
              <label className="filter">
                <span>Date Range</span>
                <button className="btn-secondary" onClick={() => setShowDatePicker(true)} type="button">
                  <span>
                    {startDate === endDate
                      ? new Date(startDate).toLocaleDateString()
                      : `${new Date(startDate).toLocaleDateString()} - ${new Date(endDate).toLocaleDateString()}`}
                  </span>
                  <FiCalendar />
                </button>
              </label>

              {permissions.canViewAllAttendance && (
                <label className="filter">
                  <span>User</span>
                  <select
                    value={selectedUserId || ''}
                    onChange={(e) => setSelectedUserId(e.target.value ? parseInt(e.target.value) : undefined)}
                  >
                    <option value="">All Users</option>
                    {users.map((dashboardUser) => (
                      <option key={dashboardUser.id} value={dashboardUser.id}>{dashboardUser.name}</option>
                    ))}
                  </select>
                </label>
              )}

              {(role === 'sales-manager' || role === 'ops') && !permissions.canViewAllAttendance && (
                <label className="filter">
                  <span>User</span>
                  <select
                    value={selectedUserId || ''}
                    onChange={(e) => setSelectedUserId(e.target.value ? parseInt(e.target.value) : undefined)}
                  >
                    <option value="">All Team Members</option>
                    <option value={user?.id ? (typeof user.id === 'string' ? parseInt(user.id) : user.id) : 0}>
                      {user?.name || 'Me'}
                    </option>
                    {teamUsers.map((dashboardUser) => (
                      <option key={dashboardUser.id} value={dashboardUser.id}>{dashboardUser.name}</option>
                    ))}
                  </select>
                </label>
              )}
            </div>
            <div className="db-header-actions">
              <button
                className="btn"
                onClick={() => {
                  if (!attendanceMarkedToday) {
                    setShowQuickCheckIn(true);
                    return;
                  }

                  if (!attendanceCheckedOutToday) {
                    setShowQuickCheckOut(true);
                  }
                }}
                disabled={checkingAttendance || (attendanceMarkedToday && attendanceCheckedOutToday)}
                title={
                  checkingAttendance
                    ? 'Checking attendance status'
                    : !attendanceMarkedToday
                      ? 'Mark your attendance'
                      : attendanceCheckedOutToday
                        ? 'Attendance already checked out for today'
                        : 'Check out'
                }
              >
                {checkingAttendance ? (
                  <>Checking...</>
                ) : !attendanceMarkedToday ? (
                  <>
                    <FiCheckSquare /> Mark attendance
                  </>
                ) : attendanceCheckedOutToday ? (
                  <>
                    <span className="attendance-icon">
                      <MdCheckCircle />
                    </span>
                    Attendance checked out
                  </>
                ) : (
                  <>
                    <FiCheckSquare /> Check-out
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        <div className="db-row">
          <StatsRow filters={filters} />
        </div>

        <div className="db-row db-grid-3">
          <div className="card">
            <QueriesComponent onCustomerClick={openCustomerModal} />
          </div>
          <div className="card">
            <FollowUpsComponent onCustomerClick={openCustomerModal} />
          </div>
          <div className="card">
            <AttendanceComponent />
          </div>
        </div>

        <div className="db-row db-grid-3">
          <div className="card">
            <DepartingPackages onCustomerClick={openCustomerModal} />
          </div>
          <PaymentsRow onCustomerClick={openCustomerModal} />
        </div>

        <div className="db-row db-grid-2">
          <div className="card">
            <ByTypeChart />
          </div>
          <div className="card">
            <ByAssigneeChart />
          </div>
        </div>

        <div className="db-row db-grid-2">
          <div className="card">
            <QueriesOverTimeComponent />
          </div>
          <div className="card">
            <BookingStackedChart />
          </div>
        </div>
      </DashboardDataProvider>

      {showDatePicker && (
        <DateRangePicker
          startDate={startDate}
          endDate={endDate}
          onDateChange={(start, end) => {
            setStartDate(start);
            setEndDate(end);
          }}
          onClose={() => setShowDatePicker(false)}
        />
      )}

      {showQuickCheckIn && (
        <QuickCheckInModal
          onClose={() => {
            setShowQuickCheckIn(false);
            void refreshTodayAttendance();
          }}
          onSuccess={() => {
            setShowQuickCheckIn(false);
            void refreshTodayAttendance();
          }}
        />
      )}

      {showQuickCheckOut && (
        <QuickCheckOutModal
          onClose={() => {
            setShowQuickCheckOut(false);
            void refreshTodayAttendance();
          }}
          onSuccess={() => {
            setShowQuickCheckOut(false);
            void refreshTodayAttendance();
          }}
        />
      )}

      {selectedCustomer && (
        <CustomerInfoModal
          customer={selectedCustomer}
          onClose={() => setSelectedCustomer(null)}
        />
      )}
    </div>
  );
};

export default SalesDashboard;
